import { SystemStyleObject, useMultiStyleConfig } from '@chakra-ui/system';
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { noop } from '@frontend/utils';

import { EMPTY_SUBMENU } from '../consts';
import { useCombobox, type UseComboboxReturn } from '../hooks/useCombobox';
import type {
  CascaderOption,
  CascaderOptionWithPath,
  CascaderProps,
  CascaderCommonProps,
  CascaderSubmenu,
  CascaderOptionGroup,
} from '../types';
import {
  isSubmenu,
  addPathsToOptions,
  toIndexPath,
  flattenOptions,
  getOptionsAtLevel,
} from '../util';

export interface CascaderState<OptionType extends CascaderOption>
  extends CascaderProps<OptionType> {
  // Core state
  inputValue: string;
  selectedPath: string[];
  cursorPath: number[];
  highlightedValue: string | null;
  isSearchMode: boolean;
  isOpen: boolean;

  // Computed values
  root: CascaderSubmenu<OptionType>;
  rootGroups: CascaderOptionGroup<OptionType>[];
  rootOptions: OptionType[];
  rootOptionsWithPaths: CascaderOptionWithPath<OptionType>[];
  flatOptions: CascaderOptionWithPath<OptionType>[];
  filteredOptions: CascaderOptionWithPath<OptionType>[];
  selectedOption?: CascaderOptionWithPath<OptionType>;

  // Combobox API
  comboboxApi: UseComboboxReturn['comboboxApi'];

  // Styles
  cascaderStyles: Record<string, SystemStyleObject>;

  // Refs
  inputRef: React.RefObject<HTMLInputElement>;

  // Actions
  setInputValue: (value: string) => void;
  setSelectedPath: (path: string[]) => void;
  setCursorPath: (path: number[]) => void;
  setHighlightedValue: (value: string | null) => void;
  handleSelect: (option: OptionType, fullPath: string[]) => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onRequestClose: () => void;
  onRequestOpen: () => void;
  getOptionsAtLevel: (level: number) => OptionType[];
  onHighlightedIndexChange: (level: number, index: number) => void;
  onMoveToParentLevel: (level: number) => void;
  onMoveToChildLevel: () => void;
  handleSelectOption: (option: OptionType, level: number) => void;
  handleClearSelection: () => void;
  focusInput: () => void;

  getCommonProps: () => CascaderCommonProps<OptionType>;
}

const CascaderStateContext =
  createContext<CascaderState<CascaderOption> | null>(null);

// The basic search filter matches any word in the input value to any word in the path
const baseSearchFilter = (inputValue: string, labels: string[]) => {
  const words = inputValue
    .toLowerCase()
    .split(' ')
    .filter((word) => word.length > 0);
  if (words.length === 0) return true;

  // Labels might have spaces in them, so we need to split them and check if any of the words match
  const splitLabels = labels.map((label) => label.split(' ')).flat();

  // Now we can check if any of the words match any of the split labels
  const { matchedWords } = splitLabels.reduce(
    (acc, optionLabel) => {
      // If we've already matched all words, return early
      if (acc.matchedWords >= words.length) return acc;

      const label = optionLabel.toLowerCase();

      // If current word matches this option, increment matched words
      if (label.includes(words[acc.matchedWords])) {
        return { ...acc, matchedWords: acc.matchedWords + 1 };
      }
      return acc;
    },
    { matchedWords: 0 },
  );

  return matchedWords === words.length;
};

// Sort by path length
const baseSort = (
  a: CascaderOptionWithPath<CascaderOption>,
  b: CascaderOptionWithPath<CascaderOption>,
): number => a.fullPath.length - b.fullPath.length;

/**
 * CascaderStateProvider is responsible for managing the state of the Cascader, unsurprisingly.
 * It ingests the props from the Cascader component, combines their functionality with the internals of
 * the Cascader, and provides state, styles, actions, and a coherent set of common props to the subcomponents for users
 * to access in their user-defined component overrides.
 */
export const CascaderStateProvider = <OptionType extends CascaderOption>({
  children,
  ...props
}: CascaderProps<OptionType> & { children: React.ReactNode }) => {
  const {
    options = EMPTY_SUBMENU as CascaderSubmenu<OptionType>,
    value,
    onChange = noop,
    placeholder = 'Select...',
    isDisabled = false,
    size = 'md',
    isClearable = false,
    inputRef: inputRefFromProps,
    positioning,
    ...rest
  } = props;
  // Core state
  const [inputValue, setInputValue] = useState('');
  const [internalSelectedPath, setSelectedPath] = useState<string[]>(
    value || [],
  );
  const selectedPath = value || internalSelectedPath;
  const [cursorPath, setCursorPath] = useState<number[]>([]);
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
  const [hoverOption, setHoverOption] = useState<OptionType | null>(null);
  const [lastInteractedType, setLastInteractedType] = useState<
    'cursor' | 'hover' | null
  >(null);

  // Input ref + focus helper
  // NB: If we don't have an inputRef from props, we create a new ref and use that.
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefFromProps ?? internalInputRef;
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  // Internal variable for tracking whether the menu should close and updating it on effect
  const [shouldClose, setShouldClose] = useState(false);

  // Computed state
  const isSearchMode = inputValue.length > 0;

  // Compute cascader styles
  const cascaderStyles = useMultiStyleConfig('Cascader', {
    size,
  });

  // Convert the root CascaderSubmenu to a flat array for search and combobox
  const rootOptions = useMemo(() => {
    return options.children.flatMap((group) => group.children);
  }, [options]);

  // Add paths to the root options
  const rootOptionsWithPaths = useMemo(() => {
    return addPathsToOptions(rootOptions);
  }, [rootOptions]);

  const flatOptionsWithSubmenus = useMemo(() => {
    return flattenOptions(rootOptionsWithPaths);
  }, [rootOptionsWithPaths]);

  // Flattened options for search and combobox collection
  const flatOptions = useMemo(
    () => flatOptionsWithSubmenus.filter((opt) => !isSubmenu(opt)),
    [flatOptionsWithSubmenus],
  );

  // Filtered options for search
  const filteredOptions = useMemo(
    () =>
      flatOptions
        .filter((opt) => baseSearchFilter(inputValue, opt.fullLabels))
        .sort(baseSort),
    [flatOptions, inputValue],
  );

  // Handle selection from menu or search
  const handleSelect = useCallback(
    (option: OptionType, fullPath: string[]) => {
      setSelectedPath(fullPath);
      setInputValue('');

      // Calculate and set the index path for the selected option
      const indexPath = toIndexPath(fullPath, rootOptions);
      setCursorPath(indexPath);
      setLastInteractedType('cursor');

      // Close the menu after selection
      setShouldClose(true);

      onChange?.(fullPath, option);
    },
    [
      onChange,
      rootOptions,
      setInputValue,
      setSelectedPath,
      setCursorPath,
      setShouldClose,
    ],
  );

  // Use the combobox hook
  const { comboboxApi, isOpen, setIsOpen } = useCombobox({
    inputRef,
    inputValue,
    disabled: isDisabled,
    placeholder,
    filteredOptions,
    positioning,
    onHighlightChange: (highlightedValue) => {
      if (isSearchMode) {
        setHighlightedValue(highlightedValue);
      }
    },
    onOpenChange: (newIsOpen) => {
      if (!newIsOpen) {
        setHighlightedValue(null);
        setCursorPath([]);
      } else {
        if (selectedPath && selectedPath.length > 0) {
          const indexPath = toIndexPath(selectedPath, rootOptions);
          if (indexPath.length > 0) {
            setCursorPath(indexPath);
          } else {
            setCursorPath([]);
          }
        } else {
          setCursorPath([]);
        }
      }
      setLastInteractedType('cursor');
    },
    onSelectOption: (option: CascaderOptionWithPath) => {
      handleSelect(option as OptionType, option.fullPath);
    },
  });

  const onRequestClose = useCallback(() => {
    setShouldClose(true);
  }, [setShouldClose]);

  const onRequestOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  // Effect to close the combobox when we request a close
  useEffect(() => {
    if (shouldClose) {
      setIsOpen(false);
      setShouldClose(false);
    }
  }, [shouldClose, setIsOpen]);

  // Update highlight on input value change
  useEffect(() => {
    if (!isSearchMode && highlightedValue) {
      setHighlightedValue(null);
    } else if (!isOpen && isSearchMode) {
      setIsOpen(true);
    }
  }, [setHighlightedValue, highlightedValue, isOpen, isSearchMode, setIsOpen]);

  // Helper function to get options at a specific level
  const getOptionsAtLevelCallback = useCallback(
    (level: number): OptionType[] => {
      return getOptionsAtLevel(rootOptions, cursorPath, level);
    },
    [rootOptions, cursorPath],
  );

  // Handle navigation within the current level
  const onHighlightedIndexChange = useCallback(
    (level: number, index: number) => {
      if (level < cursorPath.length) {
        const newCursorPath = [...cursorPath];
        newCursorPath[level] = index;
        setCursorPath(newCursorPath);
      } else if (level === 0 && cursorPath.length === 0) {
        setCursorPath([index]);
      }
      setLastInteractedType('cursor');
      focusInput();
    },
    [cursorPath, focusInput],
  );

  // Handle moving to parent level
  const onMoveToParentLevel = useCallback(
    (level: number) => {
      if (level > 0) {
        const newCursorPath = cursorPath.slice(0, level);
        setCursorPath(newCursorPath);
        setLastInteractedType('cursor');
      } else {
        setShouldClose(true);
      }

      focusInput();
    },
    [cursorPath, focusInput],
  );

  const onMoveToChildLevel = useCallback(() => {
    const newCursorPath = [...cursorPath, 0];
    setCursorPath(newCursorPath);
    setLastInteractedType('cursor');

    focusInput();
  }, [cursorPath, focusInput]);

  // Handle option selection from keyboard navigation
  const handleSelectOption = useCallback(
    (option: OptionType, level: number) => {
      const currentOptions = getOptionsAtLevelCallback(level);
      const optionIndex = currentOptions.findIndex(
        (opt) => opt.value === option.value,
      );

      if (!isSubmenu(option)) {
        const fullPath: string[] = [];

        for (let i = 0; i < level; i++) {
          const currentOptions = getOptionsAtLevelCallback(i);
          const currentIndex = cursorPath[i];
          if (currentIndex >= 0 && currentIndex < currentOptions.length) {
            const currentOption = currentOptions[currentIndex];
            fullPath.push(currentOption.value);
          }
        }

        fullPath.push(option.value);
        handleSelect(option, fullPath);
      } else {
        const newCursorPath = cursorPath.slice(0, level + 1);
        newCursorPath[level] = optionIndex;
        setCursorPath(newCursorPath);
        setLastInteractedType('cursor');

        focusInput();
      }
    },
    [cursorPath, getOptionsAtLevelCallback, handleSelect, focusInput],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        setInputValue('');
        onRequestClose();
      } else if (isOpen && !isSearchMode) {
        const activeLevel = cursorPath.length === 0 ? 0 : cursorPath.length - 1;
        const currentOptions = getOptionsAtLevelCallback(activeLevel);
        const currentHighlightedIndex = cursorPath[activeLevel];

        switch (event.key) {
          case 'ArrowUp': {
            event.preventDefault();
            if (
              currentHighlightedIndex !== undefined &&
              currentHighlightedIndex > 0
            ) {
              onHighlightedIndexChange(
                activeLevel,
                currentHighlightedIndex - 1,
              );
            }
            return;
          }

          case 'ArrowDown': {
            event.preventDefault();
            if (
              currentHighlightedIndex !== undefined &&
              currentHighlightedIndex < currentOptions.length - 1
            ) {
              onHighlightedIndexChange(
                activeLevel,
                currentHighlightedIndex + 1,
              );
            } else if (
              currentHighlightedIndex === undefined &&
              currentOptions.length > 0
            ) {
              onHighlightedIndexChange(activeLevel, 0);
            }
            return;
          }

          case 'ArrowLeft': {
            event.preventDefault();
            onMoveToParentLevel(activeLevel);
            return;
          }

          case 'ArrowRight': {
            event.preventDefault();
            if (
              currentHighlightedIndex !== undefined &&
              currentHighlightedIndex >= 0 &&
              currentHighlightedIndex < currentOptions.length
            ) {
              const highlightedOption = currentOptions[currentHighlightedIndex];
              if (isSubmenu(highlightedOption)) {
                onMoveToChildLevel();
              }
            }
            return;
          }

          case 'Enter': {
            event.preventDefault();
            if (
              currentHighlightedIndex !== undefined &&
              currentHighlightedIndex >= 0 &&
              currentHighlightedIndex < currentOptions.length
            ) {
              const highlightedOption = currentOptions[currentHighlightedIndex];
              if (!isSubmenu(highlightedOption)) {
                handleSelectOption(highlightedOption, activeLevel);
              }
            }
            return;
          }
        }
      }
    },
    [
      isOpen,
      isSearchMode,
      cursorPath,
      getOptionsAtLevelCallback,
      onHighlightedIndexChange,
      onMoveToParentLevel,
      onMoveToChildLevel,
      handleSelectOption,
      onRequestClose,
      setInputValue,
    ],
  );

  const getOptionAtPath = useCallback(
    (path: string[]) =>
      flatOptionsWithSubmenus.find(
        (opt) => opt.fullPath.join('/') === path.join('/'),
      ),
    [flatOptionsWithSubmenus],
  );

  const selectedOption = useMemo(
    () => getOptionAtPath(selectedPath),
    [selectedPath, getOptionAtPath],
  );

  const cursorOption = useMemo(() => {
    if (cursorPath.length > 0) {
      const activeLevel = cursorPath.length - 1;
      const currentOptions = getOptionsAtLevel(
        rootOptions,
        cursorPath,
        activeLevel,
      );
      const currentIndex = cursorPath[activeLevel];
      return currentOptions[currentIndex] ?? null;
    }
    return null;
  }, [cursorPath, rootOptions]);

  const handleSetHoverOption = useCallback(
    (option: OptionType | null) => {
      setHoverOption(option);
      setLastInteractedType('hover');
    },
    [setHoverOption],
  );

  const previewedOption = useMemo(() => {
    if (lastInteractedType === 'hover') {
      return hoverOption;
    } else if (cursorOption) {
      return cursorOption;
    }
    return null;
  }, [lastInteractedType, cursorOption, hoverOption]);

  const getCommonProps: () => CascaderCommonProps<OptionType> = useCallback(
    () => ({
      getSelectedValue: () => selectedPath,
      getInputValue: () => inputValue,
      hasSelectedValue: selectedPath.length > 0,
      hasInputValue: inputValue.length > 0,
      getCursorPath: () => cursorPath,
      getCursorOption: () => cursorOption,
      getHighlightedSearchValue: () => highlightedValue,
      getSelectedOption: () => selectedOption,
      getOptionAtPath: getOptionAtPath,
      getHoverOption: () => hoverOption,
      setHoverOption: handleSetHoverOption,
      getPreviewedOption: () => previewedOption,
    }),
    [
      selectedPath,
      inputValue,
      highlightedValue,
      cursorPath,
      cursorOption,
      selectedOption,
      getOptionAtPath,
      hoverOption,
      handleSetHoverOption,
      previewedOption,
    ],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedPath([]);
    setInputValue('');
    setCursorPath([]);
    setHoverOption(null);
    setLastInteractedType(null);
    setHighlightedValue(null);
    setShouldClose(true);
    onChange?.([], null);
    focusInput();
  }, [
    setSelectedPath,
    setInputValue,
    setCursorPath,
    setHoverOption,
    setLastInteractedType,
    setHighlightedValue,
    setShouldClose,
    onChange,
    focusInput,
  ]);

  const root = useMemo(() => {
    return {
      ...options,
      value: 'root',
    };
  }, [options]);

  const state: CascaderState<OptionType> = {
    // Core state
    inputValue,
    selectedPath,
    cursorPath,
    highlightedValue,
    isSearchMode,
    isOpen,

    // Computed values
    root,
    rootGroups: options.children,
    rootOptions,
    rootOptionsWithPaths,
    flatOptions,
    filteredOptions,
    selectedOption,

    // Combobox API
    comboboxApi,

    // Styles
    cascaderStyles,

    // Refs
    inputRef,

    // Actions
    setInputValue,
    setSelectedPath,
    setCursorPath,
    setHighlightedValue,
    handleSelect,
    handleInputKeyDown,
    onRequestClose,
    onRequestOpen,
    getOptionsAtLevel: getOptionsAtLevelCallback,
    onHighlightedIndexChange,
    onMoveToParentLevel,
    onMoveToChildLevel,
    handleSelectOption,
    handleClearSelection,
    focusInput,

    // Common props
    getCommonProps,

    // Props
    isClearable,
    ...rest,
  };

  return (
    <CascaderStateContext.Provider
      value={state as CascaderState<CascaderOption>}
    >
      {children}
    </CascaderStateContext.Provider>
  );
};

export const useCascaderState = <
  OptionType extends CascaderOption,
>(): CascaderState<OptionType> => {
  const context = useContext(CascaderStateContext) as CascaderState<OptionType>;
  if (!context) {
    throw new Error(
      'useCascaderState must be used within a CascaderStateProvider',
    );
  }
  return context;
};
